import { DocumentProperty } from 'types/dynamic-forms/DocumentProperty';
import { DocumentPropertyRef } from 'types/dynamic-forms/DocumentPropertyRef';
import { JsonPointerPath } from 'types/dynamic-forms/JsonPointerPath';

type PropertyReferrer = {
  /**
   * A chain of properties from the root to this referrer property (inclusive).
   */
  propsHierarchy: DocumentProperty[];

  /**
   * Which ref defintion inside the referrer property points to the referenced property.
   */
  ref: DocumentPropertyRef;
};

/**
 * Keeps track of which properties reference other properties (e.g. for visibility and validation rules).
 * Useful for precise invalidations to update only necessary parts of the document.
 */
export class DocumentDependenciesTracker {
  /**
   * Flat map of all properties to their referrers.
   * If the value is an empty array, the property is not referenced by any other property.
   */
  private depsTree: Map<DocumentProperty, PropertyReferrer[]>;

  constructor(properties: DocumentProperty[]) {
    this.depsTree = new Map<DocumentProperty, PropertyReferrer[]>();
    DocumentDependenciesTracker.flattenAllProperties(properties, this.depsTree);
    DocumentDependenciesTracker.traversePropertyReferencesForHierarchy(properties, [], properties, this.depsTree);
  }

  private static flattenAllProperties(
    properties: DocumentProperty[],
    result: Map<DocumentProperty, PropertyReferrer[]>
  ): void {
    for (const prop of properties) {
      result.set(prop, []);
      if (prop.children) {
        this.flattenAllProperties(prop.children, result);
      }
    }
  }

  private static traversePropertyReferencesForHierarchy(
    siblingProperties: DocumentProperty[],
    parentHierarchy: DocumentProperty[],
    rootProperties: DocumentProperty[],
    result: Map<DocumentProperty, PropertyReferrer[]>
  ): void {
    for (const prop of siblingProperties) {
      const myHierarchy: DocumentProperty[] = [...parentHierarchy, prop];
      if (prop.children) {
        this.traversePropertyReferencesForHierarchy(prop.children, myHierarchy, rootProperties, result);
      }

      if (!prop.refs) {
        continue;
      }

      for (const ref of prop.refs) {
        let referencedProp: DocumentProperty | undefined;
        switch (ref.refType) {
          case 'rootProp':
            referencedProp = rootProperties.find((p) => p.propertyId === ref.propertyId);
            break;
          case 'siblingProp':
            referencedProp = siblingProperties.find((p) => p.propertyId === ref.propertyId);
            break;
          default:
            throw new Error(`Unknown ref type: ${ref.refType}.`);
        }
        if (!referencedProp) {
          throw new Error(`Property ${prop.propertyId} references non-existing property ${ref.propertyId}.`);
        }
        const refs = result.get(referencedProp)!;
        refs.push({ propsHierarchy: myHierarchy, ref });
      }
    }
  }

  public collectDependentJsonPointerPaths(prop: DocumentProperty, path: JsonPointerPath, data: any): JsonPointerPath[] {
    const referrers = this.depsTree.get(prop)!;

    const pathsToCheck: JsonPointerPath[] = [];
    for (const { propsHierarchy, ref } of referrers) {
      const dependentPropId = propsHierarchy[propsHierarchy.length - 1].propertyId;

      if (ref.refType === 'siblingProp') {
        // A simple case when the dependent property is a single sibling of this property
        pathsToCheck.push([...path.slice(0, -1), dependentPropId]);
        continue;
      }
      if (ref.refType === 'rootProp') {
        // A sophisticated case when there could be tons of dependent properties inside nested arrays, all referencing this property.
        // Unfortunately, we need to explore the whole document data to find them...
        pathsToCheck.push(
          ...DocumentDependenciesTracker.findAllJsonPathsForHierarchy(
            propsHierarchy.map((p) => p.propertyId),
            data
          )
        );
        continue;
      }

      throw new Error(`Unknown ref type: "${ref.refType}".`);
    }

    return pathsToCheck;
  }

  private static findAllJsonPathsForHierarchy(propertyIdsHierarchy: string[], data: any): JsonPointerPath[] {
    let paths: JsonPointerPath[] = [[]];

    // If the dependent property is inside an array, we need to explore all array items
    for (let i = 0; i < propertyIdsHierarchy.length - 1; i++) {
      const propertyId = propertyIdsHierarchy[i];
      const newPaths: JsonPointerPath[] = [];
      for (const path of paths) {
        const previousNode = path.reduce((acc, segment) => acc?.[segment], data);
        const arrayData = previousNode?.[propertyId];
        if (Array.isArray(arrayData)) {
          for (let j = 0; j < arrayData.length; j++) {
            newPaths.push([...path, propertyId, j]);
          }
        }
        // If it's not an array, drop the hierarchy branch (no dependent property can exist here)
      }
      paths = newPaths;
    }

    const leafPropertyId = propertyIdsHierarchy[propertyIdsHierarchy.length - 1];
    for (const path of paths) {
      path.push(leafPropertyId);
    }

    return paths;
  }
}
